import { ElementRef, signal } from '@angular/core';

import { addDays, addHours } from 'date-fns';
import { BehaviorSubject, Subject, delay } from 'rxjs';

import { CamDragDropEvent, CamDragHelper, CamDragHelperConfig } from '../../engine/helpers/drag';
import { CamEventRecord } from '../../engine/models/event-record';
import { CamStoreBeforeAddEvent } from '../../engine/models/store';
import { CamSchedulerEngine } from '../../engine/scheduler-engine';
import { CamExternalEventSourceConfig } from '../../engine/types';
import { CamAddEventRequest } from '../../types/calendar';
import { SchedulerModeSetup } from '../../types/scheduler';

export class CamSchedulerData {
  private _refreshedDate$ = new Subject();

  public api: CamSchedulerEngine | null = null;
  public drag: MySchedulerDrag | null = null;

  public ready$ = new BehaviorSubject(false);

  public component: ElementRef | null = null;

  public addEventRequest$ = new Subject<CamAddEventRequest>();
  public refreshedDate$ = this._refreshedDate$.pipe(delay(50));

  public currentMode = signal<SchedulerModeSetup | null>(null);

  public getDates() {
    return {
      start: this.api?.startDate,
      end: this.api?.endDate,
    };
  }

  public isReady() {
    return this.ready$.value;
  }

  public setApi(
    api: CamSchedulerEngine,
    component: ElementRef,
    externalEventSourceFeature: CamExternalEventSourceConfig | boolean
  ) {
    this.api = api;
    this.component = component;

    this.api.on('dateRangeChange', () => {
      this._refreshedDate$.next(null);
    });

    this.api.on('modeChange', () => {
      this._refreshedDate$.next(null);
    });

    this.api.eventStore.onBeforeAdd = (event: CamStoreBeforeAddEvent<CamEventRecord>) => {
      const record = event.records[0];
      if (!record) {
        return;
      }
      const addEventRequest: CamAddEventRequest = {
        startDate: record.getData('startDate'),
        endDate: record.getData('endDate'),
        userId: record.getData('resourceId'),
        ...(record.getData('brutJson') ? JSON.parse(record.getData('brutJson')) : {}),
      };

      this.addEventRequest$.next(addEventRequest);
    };

    if (externalEventSourceFeature) {
      this.drag = new MySchedulerDrag(this.api, externalEventSourceFeature === true ? {} : externalEventSourceFeature);
    }
    queueMicrotask(() => this.ready$.next(true));
  }
  public setMode(mode: SchedulerModeSetup) {
    if (!this.api) {
      return;
    }
    this.api.setViewPreset(mode.viewPreset);

    switch (mode.viewPreset.base) {
      case 'hourAndDay':
        this.api.setTimeSpan(this.api.startDate, addDays(this.api.startDate, mode.duration));
        break;
      case 'weekAndDayLetter':
        this.api.setTimeSpan(this.api.startDate, addDays(this.api.startDate, mode.duration));
        break;
      case 'dayAndWeek':
        this.api.setTimeSpan(this.api.startDate, addDays(this.api.startDate, mode.duration));
        break;
    }

    this.currentMode.set(mode);
    this._refreshedDate$.next(null);
  }
  public destroy() {
    this.api?.destroy();
    this.api = null;
    this.component = null;
    this.drag?.destroy();
    this.drag = null;
  }
}

export class MySchedulerDrag {
  static readonly defaultConfig: Partial<CamDragHelperConfig> = {
    dropTargetSelector: '.b-timeline-subgrid',
  };

  public scheduler: CamSchedulerEngine;

  private _helper: CamDragHelper;
  private _eventData: CamEventRecord | null = null;

  constructor(scheduler: CamSchedulerEngine, externalEventSourceFeature: CamExternalEventSourceConfig) {
    this.scheduler = scheduler;
    this._helper = new CamDragHelper({
      dragRootElement: externalEventSourceFeature.dragRootElement ?? null,
      dropTargetSelector: MySchedulerDrag.defaultConfig.dropTargetSelector ?? '.b-timeline-subgrid',
      onDragStart: source => this.onDragStart(source),
      onDrop: dropEvent => this.onDrop(dropEvent),
      targetSelector: externalEventSourceFeature.dragItemSelector ?? '.bucket-item',
    });
  }

  public onDragStart = (element: HTMLElement) => {
    const duration = Number(element.dataset['duration']);
    this._eventData = new CamEventRecord({
      brutJson: element.dataset['brut'],
      duration: isNaN(duration) || !duration ? 1 : duration,
      durationUnit: element.dataset['durationUnit'] ?? 'hour',
      iconCls: element.dataset['iconCls'] ?? '',
      id: element.dataset['id'] ?? '',
      name: element.dataset['name'] ?? '',
    });
  };

  public onDrop = ({ element, pointerEvent }: CamDragDropEvent) => {
    const eventData = this._eventData;
    this._eventData = null;
    if (!eventData) {
      return;
    }

    const dropRect = element.getBoundingClientRect();
    const startDate = this.scheduler.getDateFromXY([dropRect.left, dropRect.top], 'round', false);
    eventData.setDates(
      startDate,
      this._computeEndDate(startDate, eventData.getDuration(), eventData.getDurationUnit())
    );
    eventData.setResourceId(this.scheduler.resolveResourceRecord(pointerEvent).id);

    this.scheduler.eventStore.add(eventData);
  };

  public destroy(): void {
    this._helper.destroy();
    this._eventData = null;
  }

  private _computeEndDate(startDate: Date, duration: number, durationUnit: string): Date {
    switch (durationUnit) {
      case 'hour':
        return addHours(startDate, duration);
      case 'day':
        return addDays(startDate, duration);
      default:
        return addHours(startDate, 1);
    }
  }
}
